import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import api from "Common/api";

type LeadStatus =
  | "accepted"
  | "contacted"
  | "showing_scheduled"
  | "offer_submitted"
  | "under_contract"
  | "closed_won"
  | "closed_lost";

type AssignedPropertyLead = {
  apl_id: number;
  apl_status: LeadStatus;
  apl_accepted_at: string;
  apl_status_updated_at?: string | null;
  pli_id: number;
  pli_name: string;
  pli_email: string;
  pli_phone: string;
  pli_property_type: string;
  pli_sale_or_rent: string;
  pli_state: string;
  pli_city: string;
  pli_zip: string;
  pli_country: string;
  pli_address: string;
  pli_created_at: string;
};

const statusOptions: Array<{ value: LeadStatus; label: string }> = [
  { value: "accepted", label: "Accepted" },
  { value: "contacted", label: "Contacted" },
  { value: "showing_scheduled", label: "Showing Scheduled" },
  { value: "offer_submitted", label: "Offer Submitted" },
  { value: "under_contract", label: "Under Contract" },
  { value: "closed_won", label: "Closed - Won" },
  { value: "closed_lost", label: "Closed - Lost" },
];

const statusVariant = (status: LeadStatus) => {
  if (status === "closed_won") return "success";
  if (status === "closed_lost") return "danger";
  if (status === "under_contract" || status === "offer_submitted") return "primary";
  if (status === "showing_scheduled") return "info";
  if (status === "contacted") return "warning";
  return "secondary";
};

const statusLabel = (status: LeadStatus) =>
  statusOptions.find((option) => option.value === status)?.label || status;

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "-";

export default function MyPropertyLeads() {
  const [leads, setLeads] = useState<AssignedPropertyLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response: any = await api.get("/real-estate/agent-property-leads/mine");
      setLeads(response?.result || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to load your property leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "My Property Leads | RELiBUILD";
    loadLeads();
  }, [loadLeads]);

  const updateStatus = async (assignmentId: number, status: LeadStatus) => {
    setSavingId(assignmentId);
    setError("");
    setNotice("");
    try {
      const response: any = await api.patch(
        `/real-estate/agent-property-leads/assignments/${assignmentId}/status`,
        { status }
      );
      setLeads((items) =>
        items.map((lead) =>
          lead.apl_id === assignmentId
            ? { ...lead, apl_status: status, apl_status_updated_at: new Date().toISOString() }
            : lead
        )
      );
      setNotice(response?.message || "Property lead status updated.");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to update property lead status.");
    } finally {
      setSavingId(null);
    }
  };

  const stats = useMemo(
    () => ({
      total: leads.length,
      active: leads.filter((lead) => !["closed_won", "closed_lost"].includes(lead.apl_status)).length,
      closed: leads.filter((lead) => lead.apl_status === "closed_won").length,
    }),
    [leads]
  );

  return (
    <div className="page-content">
      <Container fluid>
        {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
        {notice && <Alert variant="success" dismissible onClose={() => setNotice("")}>{notice}</Alert>}

        <Row className="g-3 mb-4">
          <Col md={4}><Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">Accepted Leads</div><h3 className="mb-0">{stats.total}</h3></Card.Body></Card></Col>
          <Col md={4}><Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">Active Pipeline</div><h3 className="mb-0">{stats.active}</h3></Card.Body></Card></Col>
          <Col md={4}><Card className="h-100 border-0 shadow-sm"><Card.Body><div className="text-muted">Closed - Won</div><h3 className="mb-0">{stats.closed}</h3></Card.Body></Card></Col>
        </Row>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div><h4 className="mb-1">Assigned clients</h4><p className="text-muted mb-0">Contact clients and keep each referral status current.</p></div>
          <Button variant="outline-primary" onClick={loadLeads} disabled={loading}>{loading ? <Spinner size="sm" /> : "Refresh"}</Button>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /><div className="text-muted mt-2">Loading assigned property leads...</div></div>
        ) : leads.length ? (
          <Row className="g-4">
            {leads.map((lead) => (
              <Col key={lead.apl_id} md={6} xl={4}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="p-4 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <Badge bg="light" text="dark">{lead.pli_sale_or_rent || "Property"}</Badge>
                      <Badge bg={statusVariant(lead.apl_status)}>{statusLabel(lead.apl_status)}</Badge>
                    </div>
                    <h5 className="mt-3 mb-1">{lead.pli_property_type || "Property opportunity"}</h5>
                    <div className="small text-muted">{[lead.pli_address, lead.pli_city, lead.pli_state, lead.pli_zip].filter(Boolean).join(", ")}</div>
                    <hr />
                    <div className="fw-semibold">{lead.pli_name}</div>
                    <a className="small" href={`mailto:${lead.pli_email}`}>{lead.pli_email}</a>
                    <a className="small" href={`tel:${lead.pli_phone}`}>{lead.pli_phone}</a>
                    <div className="small text-muted mt-2">Accepted {formatDate(lead.apl_accepted_at)}</div>

                    <Form.Group className="mt-4">
                      <Form.Label className="small fw-semibold">Lead status</Form.Label>
                      <Form.Select
                        value={lead.apl_status}
                        disabled={savingId === lead.apl_id}
                        onChange={(event) => updateStatus(lead.apl_id, event.target.value as LeadStatus)}
                      >
                        {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </Form.Select>
                      {savingId === lead.apl_id && <Form.Text className="text-muted">Saving status...</Form.Text>}
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Card className="border-0 shadow-sm"><Card.Body className="text-center py-5"><h5>No accepted property leads yet</h5><p className="text-muted mb-0">Leads you accept will appear here.</p></Card.Body></Card>
        )}
      </Container>
    </div>
  );
}
